/**
 * SortGroupControls Component
 * 
 * Controls for sorting and grouping collection items.
 * Includes sort field dropdown, direction toggle, and group by dropdown.
 */

import { useState, useRef, useEffect } from 'react';
import { 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown, 
  Layers, 
  ChevronDown,
  Check 
} from 'lucide-react';
import type { SortGroupControlsProps, SortOption, GroupOption, SortDirection } from './types';

// ============================================================================
// Dropdown Component
// ============================================================================

interface DropdownProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

function Dropdown({ trigger, children, isOpen, onToggle }: DropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        if (isOpen) onToggle();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);
  
  return (
    <div className="relative" ref={dropdownRef}>
      <div onClick={onToggle}>{trigger}</div>
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 min-w-48 bg-[var(--bg-raised)] shadow-lg border border-[var(--rule)] z-50 py-1">
          {children}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Sort Controls
// ============================================================================

interface SortControlsProps<T = string> {
  options: SortOption<T>[];
  currentField: T;
  currentDirection: SortDirection;
  onSortChange: (field: T, direction?: SortDirection) => void;
}

function SortControls<T = string>({
  options,
  currentField,
  currentDirection,
  onSortChange,
}: SortControlsProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  
  const currentOption = options.find(o => o.field === currentField);
  const DirectionIcon = currentDirection === 'asc' ? ArrowUp : ArrowDown;
  
  const handleFieldSelect = (field: T) => {
    const option = options.find(o => o.field === field);
    const direction = option?.defaultDirection || 'asc';
    onSortChange(field, field === currentField ? undefined : direction);
    setIsOpen(false);
  };
  
  const handleDirectionToggle = () => {
    onSortChange(currentField, currentDirection === 'asc' ? 'desc' : 'asc');
  };
  
  return (
    <div className="flex items-center gap-1">
      <Dropdown
        isOpen={isOpen}
        onToggle={() => setIsOpen(!isOpen)}
        trigger={
          <button
            type="button"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--bg-raised)] border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <ArrowUpDown className="h-4 w-4" />
            <span>Sort: {currentOption?.label || 'None'}</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        }
      >
        {options.map((option) => (
          <button
            key={String(option.field)}
            type="button"
            onClick={() => handleFieldSelect(option.field)}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left
                       hover:bg-[var(--surface)] transition-colors
                       ${option.field === currentField ? 'bg-primary-50 text-primary-700' : 'text-[var(--text-sec)]'}`}
          >
            <span className="flex-1">{option.label}</span>
            {option.field === currentField && <Check className="h-4 w-4" />}
          </button>
        ))}
      </Dropdown>
      
      {/* Direction toggle */}
      <button
        type="button"
        onClick={handleDirectionToggle}
        className="p-1.5 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] focus:outline-none focus:ring-2 focus:ring-primary-500"
        title={currentDirection === 'asc' ? 'Ascending' : 'Descending'}
      >
        <DirectionIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

// ============================================================================
// Group Controls
// ============================================================================

interface GroupControlsProps<T = string> {
  options: GroupOption<T>[];
  currentGroupBy: T | null;
  onGroupByChange: (field: T | null) => void;
}

function GroupControls<T = string>({
  options,
  currentGroupBy,
  onGroupByChange,
}: GroupControlsProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  
  const currentOption = options.find(o => o.field === currentGroupBy);
  const isGrouped = currentGroupBy !== null;
  
  const handleSelect = (field: T | null) => {
    onGroupByChange(field);
    setIsOpen(false);
  };
  
  return (
    <Dropdown
      isOpen={isOpen}
      onToggle={() => setIsOpen(!isOpen)}
      trigger={
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm 
                     border focus:outline-none focus:ring-2 focus:ring-primary-500
                     ${isGrouped 
                       ? 'bg-primary-50 border-primary-300 text-primary-700 hover:bg-primary-100' 
                       : 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]'}`}
        >
          <Layers className="h-4 w-4" />
          <span>Group: {currentOption?.label || 'None'}</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      }
    >
      {/* None option */}
      <button
        type="button"
        onClick={() => handleSelect(null)}
        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left
                   hover:bg-[var(--surface)] transition-colors
                   ${currentGroupBy === null ? 'bg-primary-50 text-primary-700' : 'text-[var(--text-sec)]'}`}
      >
        <span className="flex-1">No grouping</span>
        {currentGroupBy === null && <Check className="h-4 w-4" />}
      </button>
      
      <div className="border-t border-[var(--rule)] my-1" />
      
      {options.map((option) => (
        <button
          key={String(option.field)}
          type="button"
          onClick={() => handleSelect(option.field)}
          className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left
                     hover:bg-[var(--surface)] transition-colors
                     ${option.field === currentGroupBy ? 'bg-primary-50 text-primary-700' : 'text-[var(--text-sec)]'}`}
        >
          <span className="flex-1">{option.label}</span>
          {option.field === currentGroupBy && <Check className="h-4 w-4" />}
        </button>
      ))}
    </Dropdown>
  );
}

// ============================================================================
// SortGroupControls Component
// ============================================================================

export function SortGroupControls<SortField = string, GroupField = string>({
  sortOptions,
  currentSort,
  onSortChange,
  groupOptions = [],
  currentGroupBy,
  onGroupByChange,
  className = '',
}: SortGroupControlsProps<SortField, GroupField>) {
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      {/* Sort controls */}
      {sortOptions.length > 0 && (
        <SortControls
          options={sortOptions}
          currentField={currentSort.field}
          currentDirection={currentSort.direction}
          onSortChange={onSortChange}
        />
      )}
      
      {/* Group controls */}
      {groupOptions.length > 0 && (
        <GroupControls
          options={groupOptions}
          currentGroupBy={currentGroupBy}
          onGroupByChange={onGroupByChange}
        />
      )}
    </div>
  );
}

// ============================================================================
// Collapsible Group Header
// ============================================================================

interface GroupHeaderProps {
  label: string;
  count: number;
  isExpanded: boolean;
  onToggle: () => void;
}

export function GroupHeader({ label, count, isExpanded, onToggle }: GroupHeaderProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center gap-2 px-4 py-2 bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
    >
      <ChevronDown 
        className={`h-4 w-4 text-[var(--text-sec)] transition-transform ${isExpanded ? '' : '-rotate-90'}`} 
      />
      <span className="font-medium text-[var(--text-pri)]">{label}</span>
      <span className="text-sm text-[var(--text-ter)]">({count})</span>
    </button>
  );
}

export default SortGroupControls;
